import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { debounce } from 'lodash';

import { getSummaryActiveItem } from '../../MyComponents/SummaryMenu/summaryUtils';
import { UPDATE_TUTORIAL_ACTIVE_SECTION } from '../../redux/actionTypes';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';
import useTutorialOffsets from './useTutorialOffsets';

const useStyles = makeStyles(() => ({
    // Wide screens: a card beside the content that stays in view while reading.
    // It pins under whatever the layout keeps above the page (see the hook).
    aside: {
        boxSizing: 'border-box',
        position: 'sticky',
        top: ({ asideTop }) => `${asideTop}px`,
        maxHeight: ({ asideTop }) => `calc(100vh - ${asideTop + 20}px)`,
        overflowY: 'auto',
        padding: '18px 10px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
    },
    // Narrow screens: a bar pinned under the nav showing where the reader is,
    // opening into the full list on tap.
    bar: {
        boxSizing: 'border-box',
        position: 'sticky',
        top: ({ barTop }) => `${barTop}px`,
        zIndex: 20,
        marginBottom: '18px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '18px',
        background: REDESIGN_COLORS.white,
        boxShadow: '0 12px 32px rgba(6, 63, 69, 0.09)',
    },
    barButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        minHeight: '52px',
        padding: '0 16px',
        border: 0,
        background: 'none',
        fontFamily: REDESIGN_FONTS.body,
        textAlign: 'left',
        cursor: 'pointer',
    },
    barLabel: {
        flexShrink: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    barCurrent: {
        flex: 1,
        overflow: 'hidden',
        color: REDESIGN_COLORS.dark,
        fontSize: '0.95rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    chevron: {
        flexShrink: 0,
        transition: 'transform 150ms ease',
    },
    chevronOpen: {
        transform: 'rotate(180deg)',
    },
    barList: {
        maxHeight: '60vh',
        overflowY: 'auto',
        padding: '0 10px 12px',
        borderTop: `1px solid ${REDESIGN_COLORS.sand}`,
    },
    heading: {
        margin: '0 14px 10px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    list: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    item: {
        display: 'block',
        width: '100%',
        padding: '8px 14px',
        border: 0,
        borderRadius: '12px',
        background: 'none',
        color: REDESIGN_COLORS.dark,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        lineHeight: 1.3,
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    child: {
        padding: '6px 14px 6px 26px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.86rem',
        fontWeight: 600,
    },
    active: {
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
    },
    group: {
        marginTop: '6px',
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

// Entries without a label are anchors only; the old menus skipped them too.
const labelled = (nodes) => (nodes || []).filter((node) => node && node.nameId);

const findNode = (sections, id) => {
    for (const section of sections) {
        if (section.id === id) return section;
        const child = (section.children || []).find((c) => c.id === id);
        if (child) return child;
    }
    return null;
};

/**
 * The tutorial's table of contents. `variant="aside"` is the always-visible
 * sticky list beside the content; `variant="bar"` is the collapsed sticky bar
 * for narrow screens. Both track the section in view while scrolling and,
 * on click, push the section id as location state, which the page scrolls to.
 */
const TutorialToc = ({ sections, variant = 'aside' }) => {
    const classes = useStyles(useTutorialOffsets());
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const activeId = useSelector((state) => state.tutorial.sectionId);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const track = debounce(() => {
            const item = getSummaryActiveItem(sections);
            if (item && item.id) {
                dispatch({ type: UPDATE_TUTORIAL_ACTIVE_SECTION, payload: { sectionId: item.id } });
            }
        }, 100);
        window.addEventListener('scroll', track);
        return () => {
            window.removeEventListener('scroll', track);
            track.cancel();
        };
    }, [sections, dispatch]);

    const go = (id) => {
        // Same path, so the language prefix in the URL is kept.
        history.push({ pathname: location.pathname, state: { sectionId: id } });
        dispatch({ type: UPDATE_TUTORIAL_ACTIVE_SECTION, payload: { sectionId: id } });
        setOpen(false);
    };

    const list = (
        <ul className={classes.list}>
            {labelled(sections).map((section) => (
                <li key={section.id} className={classes.group}>
                    <button
                        type="button"
                        onClick={() => go(section.id)}
                        className={cx(classes.item, activeId === section.id && classes.active)}
                    >
                        <FormattedMessage id={section.nameId} />
                    </button>
                    {labelled(section.children).length ? (
                        <ul className={classes.list}>
                            {labelled(section.children).map((child) => (
                                <li key={child.id}>
                                    <button
                                        type="button"
                                        onClick={() => go(child.id)}
                                        className={cx(classes.item, classes.child, activeId === child.id && classes.active)}
                                    >
                                        <FormattedMessage id={child.nameId} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </li>
            ))}
        </ul>
    );

    if (variant === 'aside') {
        return (
            <nav className={classes.aside}>
                <div className={classes.heading}>
                    <FormattedMessage id="redesign.content" />
                </div>
                {list}
            </nav>
        );
    }

    const current = findNode(sections, activeId);
    const first = labelled(sections)[0];

    return (
        <nav className={classes.bar}>
            <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className={classes.barButton}>
                <span className={classes.barLabel}>
                    <FormattedMessage id="redesign.content" />
                </span>
                <span className={classes.barCurrent}>
                    {current && current.nameId ? (
                        <FormattedMessage id={current.nameId} />
                    ) : first ? (
                        <FormattedMessage id={first.nameId} />
                    ) : null}
                </span>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={cx(classes.chevron, open && classes.chevronOpen)}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open ? <div className={classes.barList}>{list}</div> : null}
        </nav>
    );
};

export default TutorialToc;
